import useVuelidate, { ErrorObject, ServerErrors, Validation, ValidationArgs } from '@vuelidate/core';
import { AxiosError, AxiosResponse } from 'axios';
import { ComputedRef, Ref, UnwrapNestedRefs, computed, isRef, reactive, ref, unref } from 'vue';

export interface FormComposable<T> {
  fields: T | Ref<T>;
  validation: Ref<Validation<ValidationArgs<T>, T>>;
  submitting: boolean;
  fieldErrors: ComputedRef<(field: keyof T) => string[]>;
  setFieldError: (fieldName: keyof T, message: string) => void;
  clearValidationErrors: () => void;
  resetValidation: () => void;
  submit: (submitter: (fields: T) => any) => Promise<any>;
}

type ValidationRules<T> = Partial<ValidationArgs<T>> | ComputedRef<Partial<ValidationArgs<T>>>;
type ExternalResults<T> = { [key in keyof T]: string[] };

export function useForm<T>(
  fields: T | Ref<T>,
  validationRules: ValidationArgs | ComputedRef<ValidationArgs> = {},
): UnwrapNestedRefs<FormComposable<T>> {
  const $externalResults = reactive<ExternalResults<T>>({} as ExternalResults<T>);
  const state: Ref<T> = isRef(fields) ? fields : (ref(fields) as Ref<T>);
  const rules = processValidationRules<T>(state, validationRules);
  const validation = useVuelidate(rules, state, { $externalResults: $externalResults as ServerErrors, $scope: false });

  return reactive<FormComposable<T>>({
    fields,
    validation,
    submitting: false,
    fieldErrors: computed(() => (field: keyof T) => {
      return validation.value[field]?.$errors?.map((error: ErrorObject) => error.$message) || [];
    }),

    setFieldError(fieldName: keyof T, message: string) {
      Object.assign($externalResults, { [fieldName]: [message] });
    },

    clearValidationErrors() {
      validation.value.$clearExternalResults();
    },

    resetValidation() {
      this.clearValidationErrors();
      validation.value.$reset();
    },

    async submit(submitter: (fields: T) => Promise<AxiosResponse>): Promise<AxiosResponse> {
      this.clearValidationErrors();
      validation.value.$touch();

      if (this.submitting) {
        return Promise.reject('Form submition is pending');
      }

      if (!(await validation.value.$validate())) {
        return Promise.reject('Form is invalid');
      }

      this.submitting = true;

      try {
        return await submitter(unref(fields));
      } catch (error) {
        if (!(error instanceof AxiosError)) {
          console.error(error);
          throw error;
        }

        const { data } = error.response as AxiosResponse<{
          validationErrors?: { [key in keyof T]: string[] };
          message?: string;
        }>;

        if (data.validationErrors) {
          Object.assign($externalResults, { ...data.validationErrors });
        } else {
          console.error(error);
        }

        throw error;
      } finally {
        this.submitting = false;
      }
    },
  });
}

/*
Issue: https://github.com/vuelidate/vuelidate/issues/1209
Description: vuelidate ignores server errors if field is not present in the validation rules or field validation rules is empty.
Workaround:
- add field validation rule if it's not presented.
- add dummy validation rule if validation rules are empty.
*/
function processValidationRules<T>(
  fields: Ref<T>,
  validationRules: ValidationRules<T>,
): ComputedRef<ValidationArgs<T>> {
  const externalServerValidation = () => true;

  return computed<ValidationArgs<T>>(() => {
    const rules = unref(validationRules) as ValidationArgs<T>;

    for (const fieldName in unref(fields)) {
      if (!Object.prototype.hasOwnProperty.call(rules, fieldName) || !Object.keys(rules[fieldName]).length) {
        rules[fieldName] = { externalServerValidation };
      }
    }

    return rules as ValidationArgs<T>;
  });
}
